import {
  PurchaseLoadSortOption,
  PurchaseLoadStatus,
  WeightInput,
} from '@rematter/frontend-lib-api-client';

import { InboundTransactionRecord } from '../types/transaction';
import { derivePaymentStatus } from '../utils/paymentStatus';
import { calculateTotalNetWeight, LineItemForTotals } from '../utils/weight';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export const transactionsInboundQueryAtom = createQueryAtomFactory(
  'trade.transactionsInbound',
  (params: {
    searchTerm?: string;
    facilityId?: string;
    facilityIds?: string[];
    supplierAccountIds?: string[];
    status?: PurchaseLoadStatus[];
    limit?: number;
    offset?: number;
    sorting?: PurchaseLoadSortOption[];
    fromReceivedDate?: string;
    toReceivedDate?: string;
  }) => ({
    purchaseLoads: {
      __args: {
        filter: {
          searchTerm: params.searchTerm,
          facilityId: params.facilityId,
          facilityIds: params.facilityIds,
          supplierAccountIds: params.supplierAccountIds,
          status: params.status,
          fromReceivedDate: params.fromReceivedDate,
          toReceivedDate: params.toReceivedDate,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        status: true,
        documentNumber: true,
        receivedDate: true,
        purchaseOrderId: true,
        purchaseOrder: {
          documentNumber: true,
        },
        externalNotes: true,
        facilityId: true,
        facility: {
          id: true,
          facilityName: true,
        },
        supplierAccountId: true,
        supplierAccount: {
          id: true,
          name: true,
          type: true,
        },
        bill: {
          id: true,
          documentNumber: true,
          status: true,
          amount: {
            amount: true,
          },
          openAmount: {
            amount: true,
            currencyCode: true,
          },
        },
        grandTotal: {
          amount: true,
          currencyCode: true,
        },
        lineItems: {
          id: true,
          netWeight: {
            value: true,
            unit: true,
          },
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      purchaseLoads: InboundTransactionRecord[];
      totalCount: number;
    } => {
      if (!data?.purchaseLoads?.entries?.length) {
        return { purchaseLoads: [], totalCount: 0 };
      }

      return {
        purchaseLoads: data.purchaseLoads.entries.map((purchaseLoad) => {
          const lineItems = purchaseLoad.lineItems || [];

          const mappedLineItems: LineItemForTotals[] = lineItems.map((item) => {
            const net = item.netWeight as WeightInput | null;
            return {
              quantity: net?.value ?? null,
              unit: net?.unit ?? null,
            };
          });
          const totalNetWeight = calculateTotalNetWeight(mappedLineItems);

          return {
            id: purchaseLoad.id,
            status: purchaseLoad.status,
            documentNumber: purchaseLoad.documentNumber,
            receivedDate: parseZonedDateTime(purchaseLoad.receivedDate),
            purchaseOrderId: purchaseLoad.purchaseOrderId || null,
            purchaseOrderNumber:
              purchaseLoad.purchaseOrder?.documentNumber ?? undefined,
            externalNotes: purchaseLoad.externalNotes ?? undefined,
            billId: purchaseLoad.bill?.id ?? undefined,
            billNumber: purchaseLoad.bill?.documentNumber ?? undefined,
            facilityId: purchaseLoad.facilityId,
            facility: purchaseLoad.facility,
            supplierAccountId: purchaseLoad.supplierAccountId || null,
            supplierAccount: purchaseLoad.supplierAccount || null,
            supplierName: purchaseLoad.supplierAccount?.name || undefined,
            lineItemCount: lineItems.length,
            totalNetWeight,
            grandTotal: purchaseLoad.grandTotal ?? null,
            outstandingBalance: purchaseLoad.bill?.openAmount ?? null,
            paymentStatus: derivePaymentStatus(purchaseLoad.bill),
            timestamps: {
              createdAt: parseZonedDateTime(purchaseLoad.timestamps.createdAt),
              updatedAt: parseZonedDateTime(purchaseLoad.timestamps.updatedAt),
            },
          };
        }),
        totalCount: data.purchaseLoads.page?.total || 0,
      };
    },
  },
);
